import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { LinearGradient } from '@/components/LinearGradient'
import { useTheme } from '@/hooks/useTheme'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { currencyUtils } from '@/utils'
import { Transaction, transactionServices } from '@/domain/transactions'

interface TransactionItemProps {
  transaction: Transaction & {
    currencyIcon?: string
    currencyColor?: string
    currencyName?: string
    currencySymbol?: string
    // Target currency data for conversions
    targetCurrencyIcon?: string
    targetCurrencyColor?: string
    targetCurrencyName?: string
    targetCurrencySymbol?: string
    // Formatted amounts
    formattedFiatAmount?: string
    formattedCreditAmount?: string
    formattedFiatCreditAmount?: string
  }
  showDate?: boolean
}

export const TransactionItem: React.FC<TransactionItemProps> = ({
  transaction,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  const title = transactionServices.getTransactionTitle(transaction, t)
  const isOutgoing = transaction.direction === 'send'
  const isPending = transaction.state === 'processing'
  const isFailed = ['declined', 'cancelled'].includes(transaction.state)
  const isRefunded = transaction.state === 'refunded'
  const isFailedOrRefunded = isFailed || isRefunded
  const isConvert =
    transaction.type === 'swap_convert' || transaction.type === 'swap_sell'

  // Generate avatar data with enhanced currency support
  const getAvatarData = () => {
    const currency = transaction.amount?.currency || ''

    switch (transaction.type) {
      case 'kem_to_kem':
      case 'on_ramp_send':
        return {
          type: 'user',
          uri: transaction.subject_user?.avatar_path,
          name: transaction.subject_user?.first_name || 'U',
          backgroundColor: transaction.currencyColor || '#6366F1',
        }

      case 'card_write_off':
        return {
          type: 'icon',
          icon: 'card' as const,
          backgroundColor: '#8B5CF6',
        }

      case 'on_ramp_top_up':
        // Show currency icon for top-ups (like in RN)
        return {
          type: 'currency',
          name: currency.charAt(0) || '?',
          backgroundColor: transaction.currencyColor || '#10B981',
          icon: transaction.currencyIcon,
        }

      case 'external_withdraw':
        // Show currency icon for withdrawals (like in RN)
        return {
          type: 'currency',
          name: currency.charAt(0) || '?',
          backgroundColor: transaction.currencyColor || '#F59E0B',
          icon: transaction.currencyIcon,
        }

      case 'swap_convert':
        // For conversions, show target currency icon like in RN
        if (transaction.subject_swap?.credit_amount?.currency) {
          const targetCurrency = transaction.subject_swap.credit_amount.currency
          return {
            type: 'currency',
            name: targetCurrency.charAt(0) || '?',
            backgroundColor: transaction.currencyColor || '#3B82F6',
            icon: transaction.currencyIcon,
          }
        }
        return {
          type: 'icon',
          icon: 'refresh' as const,
          backgroundColor: '#3B82F6',
        }

      case 'external_deposit':
        // Show currency icon for deposits (like in RN)
        return {
          type: 'currency',
          name: currency.charAt(0) || '?',
          backgroundColor: transaction.currencyColor || '#10B981',
          icon: transaction.currencyIcon,
        }

      case 'saving_transfer_in':
        return {
          type: 'icon',
          icon: 'arrowDownCircle' as const,
          backgroundColor: theme.colors.background.secondaryBlue,
        }

      case 'saving_transfer_out':
        return {
          type: 'icon',
          icon: 'arrowRightCircle' as const,
          backgroundColor: theme.colors.background.savingTransferOut,
        }

      default:
        return {
          type: 'currency',
          name: currency[0] || '?',
          backgroundColor: transaction.currencyColor || '#6B7280',
          icon: transaction.currencyIcon,
        }
    }
  }

  const avatarData = getAvatarData()

  const formatDate = (date: Date | number | string) => {
    // Convert to Date object if needed
    let dateObj: Date

    if (date instanceof Date) {
      dateObj = date
    } else if (typeof date === 'number') {
      // Assume timestamp in seconds, convert to milliseconds
      dateObj = new Date(date * 1000)
    } else if (typeof date === 'string') {
      dateObj = new Date(date)
    } else {
      // Fallback to current date if invalid
      dateObj = new Date()
    }

    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date'
    }

    // Format only time like in React Native version when isTitle: true and showTime: true
    return dateObj
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace('AM', 'am')
      .replace('PM', 'pm')
  }

  const handlePress = () => {
    navigate(`/transaction-details/${transaction.transaction_id}`)
  }

  return (
    <BaseView
      row
      align="center"
      py={12}
      px={12}
      gap={12}
      bgColor="background.cardItemRoot"
      rounded={26}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onClick={handlePress}
    >
      {/* Avatar */}
      <LinearGradient
        colors={
          Array.isArray(avatarData.backgroundColor)
            ? avatarData.backgroundColor
            : [avatarData.backgroundColor, avatarData.backgroundColor]
        }
        locations={
          Array.isArray(avatarData.backgroundColor) &&
          avatarData.backgroundColor.length === 3
            ? [0, 0.15, 0.8]
            : undefined
        }
        start={{ x: 0.1, y: 0 }}
        end={{ x: 1, y: 1 }}
        circle
        circleSize={50}
        center
        style={{
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {avatarData.type === 'user' && avatarData.uri ? (
          <img
            src={avatarData.uri}
            alt={avatarData.name || 'User'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : avatarData.type === 'icon' && avatarData.icon ? (
          <AppIcons name={avatarData.icon} size={28} color="#FFFFFF" />
        ) : avatarData.type === 'currency' && avatarData.icon ? (
          <img
            src={avatarData.icon}
            alt={transaction.currencyName || transaction.amount?.currency || ''}
            style={{
              width: '32px',
              height: '32px',
              objectFit: 'contain', // Используем contain вместо cover
              borderRadius: '0', // Убираем скругление для валют
            }}
          />
        ) : (
          <BaseText fontSize={16} fontFamily="semibold" color="#FFFFFF">
            {avatarData.name || '?'}
          </BaseText>
        )}
      </LinearGradient>

      {/* Content */}
      <BaseView
        gap={2}
        style={{
          flex: 1,
          minWidth: 0, // Важно для правильного сжатия
        }}
      >
        <BaseText
          fontSize={16}
          fontFamily="semibold"
          color="label.default"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </BaseText>

        {isFailedOrRefunded && (
          <BaseView row align="center" gap={4}>
            <AppIcons
              name="triangleWarning"
              size={16}
              color="icon.disabledInputIcon"
            />
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
            >
              {isFailed
                ? t('transactions:states.failed')
                : t('transactions:states.refunded')}
            </BaseText>
          </BaseView>
        )}

        {!isFailedOrRefunded && (
          <BaseText
            fontSize={14}
            fontFamily="semibold"
            color="label.description"
          >
            {formatDate(transaction.created_at)}
          </BaseText>
        )}
      </BaseView>

      {/* Footer - Amount */}
      <BaseView
        align="flex-end"
        style={{
          paddingLeft: 4,
          flexShrink: 0, // Не сжимается
          minWidth: 120, // Минимальная ширина для суммы
        }}
      >
        {isPending && (
          <BaseView gap={3} align="flex-end">
            <BaseText fontSize={16} fontFamily="medium" color="label.default">
              {transaction.formattedCreditAmount || transaction.formattedAmount}
            </BaseText>
            <BaseText
              fontSize={14}
              fontFamily="regular"
              color="label.description"
            >
              {t('translation:pending') || 'Pending'}
            </BaseText>
          </BaseView>
        )}

        {!isPending && (
          <BaseText
            fontSize={16}
            fontFamily="semibold"
            style={{
              color: isFailedOrRefunded
                ? theme.colors.label.disabled
                : !isOutgoing
                  ? theme.colors.label.flagProfit
                  : theme.colors.label.default,
              textDecorationLine: isFailedOrRefunded ? 'line-through' : 'none',
            }}
          >
            {transaction.formattedCreditAmount || transaction.formattedAmount}
          </BaseText>
        )}

        {!isFailedOrRefunded &&
          !isPending &&
          // For conversions, show the original amount (like in RN), not fiat
          ((isConvert && transaction.formattedAmount) ||
            transaction.formattedFiatAmount ||
            transaction.amount?.fiat_amount) && (
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.description"
            >
              {isConvert
                ? // For conversions, show minus sign with original amount (like in RN)
                  `-${transaction.formattedAmount}`
                : // For other transactions, show fiat equivalent
                  `≈${
                    transaction.formattedFiatAmount ||
                    currencyUtils.formatFiatCurrency({
                      value: Number(transaction.amount?.fiat_amount || 0),
                      fiatCode: displayCurrency?.isin || 'USD',
                      showDecimals: true,
                    })
                  }`}
            </BaseText>
          )}
      </BaseView>
    </BaseView>
  )
}
